import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { DocumentFileList } from '@/common/components/DocumentFileList'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import Table from '@/common/components/Table'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import { Mapper } from '@/common/services/mapper'
import { MessageData } from '@/common/types/support/message.type'
import { TicketData } from '@/common/types/support/ticket.type'
import { cn } from '@/common/utils/cn'
import { FormTransformers } from '@/common/utils/formTransformers'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { useSupportTicket } from '@/features/support/external/hooks/useSupportTicket'
import { useSupportTicketMessages } from '@/features/support/external/hooks/useSupportTicketMessages'
import { useSupportReasons } from '@/features/support/hooks/useSupportReasons'
import { ModalAddSupportTickets } from '@/features/support/internal/components/ModalAddSupportTickets'
import { ModalAddUsers } from '@/features/support/internal/components/ModalAddUsers'
import { supportStatusStrategy } from '@/features/support/internal/constants/supportStatusOptions.constant'
import { supportTicketColumns } from '@/features/support/internal/constants/supportTicketColumns.constant'
import { userColumns } from '@/features/support/internal/constants/userColumns.constant'
import { useUpdateInternalSupportTicket } from '@/features/support/internal/hooks/useUpdateInternalSupportTicket'
import {
  internalSupportTicketSchema,
  InternalSupportTicketValidationSchema,
} from '@/features/support/internal/schemas/create.schema'
import { InternalSupportTicketsTransformer } from '@/features/support/internal/services/internalSupportTicketsTransformer'
import { SupportTicketAvailableUserWithRelationType } from '@/features/support/internal/types/supportTicketAvailableUser.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeftIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

const InternalSupportUpdate = () => {
  const { id: supportTicketId } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [users, setUsers] = useState<SupportTicketAvailableUserWithRelationType[]>([])
  const [supportTickets, setSupportTickets] = useState<TicketData[]>([])
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchSupportReason, setSearchSupportReason] = useDebounce('')

  const { data: supportTicketData, isPending: supportTicketDataIsPending } = useSupportTicket({
    id: supportTicketId || '',
  })

  const { data: supportTicketMessagesData, isPending: supportTicketMessagesDataIsPending } = useSupportTicketMessages<
    MessageData[]
  >({
    id: supportTicketId || '',
  })

  const { data: supportReasonOptions, isPending: supportReasonsIsPending } = useSupportReasons({
    filters: { search: searchSupportReason },
    mapper: Mapper.mapToOptions({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const {
    register,
    control,
    setError,
    reset,
    formState: { errors },
  } = useForm<InternalSupportTicketValidationSchema>({
    resolver: zodResolver(internalSupportTicketSchema),
    defaultValues: {
      reason: supportTicketData?.reason?.id
        ? { value: supportTicketData?.reason?.id, label: supportTicketData?.reason?.display_name }
        : undefined,
      subject_line: supportTicketData?.subject_line || '',
      content: supportTicketData?.first_message?.content || '',
    },
  })

  const { mutate: updateSupportTicket, isPending: updateSupportTicketIsPending } = useUpdateInternalSupportTicket({
    supportTickerId: supportTicketId || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internalSupportTickets'] })
      goBack({ fallback: `/internal-support/${supportTicketId}` })
    },
    onError: FormTransformers.errorsTransformer<InternalSupportTicketValidationSchema>({ setError, setMessages }),
  })

  const onSubmit = () => {
    const responsibleUsers = users?.filter((user) => user.relationType === 'responsible')

    if (!responsibleUsers?.length) {
      return setMessages(['O chamado deve ter ao menos um usuário responsável.'])
    }

    if (responsibleUsers?.length > 1) {
      return setMessages(['O chamado deve ter apenas um usuário responsável.'])
    }

    updateSupportTicket({ users, supportTickets, supportTicketData })
  }

  useEffect(() => {
    if (supportTicketDataIsPending || supportTicketMessagesDataIsPending || supportReasonsIsPending) return

    setUsers(
      supportTicketData?.user_tickets.map((user) => ({
        ...user.user,
        relationType: user.relation_type,
      })) || [],
    )

    setSupportTickets(supportTicketData?.outgoing_ticket_linkings?.map((linking) => linking.target_ticket) || [])

    reset({
      reason: supportTicketData?.reason?.id
        ? { value: supportTicketData?.reason?.id, label: supportTicketData?.reason?.display_name }
        : undefined,
      subject_line: supportTicketData?.subject_line || '',
      content: supportTicketMessagesData?.[0]?.content || '',
    })
  }, [supportTicketDataIsPending, supportTicketMessagesDataIsPending, supportReasonsIsPending])

  return (
    <>
      {supportTicketDataIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div className="flex gap-4 items-center mb-6">
              <Button variant="link-white" onClick={() => goBack({ fallback: `/internal-support/${supportTicketId}` })}>
                <ChevronLeftIcon size={40} strokeWidth={1} />
              </Button>

              <div className="flex gap-2 items-center">
                <h2 className="text-2xl font-bold">{supportTicketData?.reference_number}</h2>

                <div
                  data-testid="status_badge"
                  className={cn(
                    'px-2 py-1 rounded-sm',
                    supportTicketData?.status &&
                      ['pending', 'awaiting_tcard_response', 'not_responded', 'in_progress'].includes(
                        supportTicketData?.status,
                      ) &&
                      'bg-yellow-op-2',
                    supportTicketData?.status &&
                      ['awaiting_customer_response', 'closed'].includes(supportTicketData?.status) &&
                      'bg-blue-200',
                  )}
                >
                  {supportStatusStrategy[supportTicketData?.status as keyof typeof supportStatusStrategy]}
                </div>
              </div>
            </div>

            <div className="flex gap-x-4">
              <Button
                type="button"
                variant="outline-error"
                onClick={() => goBack({ fallback: `/internal-support/${supportTicketId}` })}
                disabled={updateSupportTicketIsPending}
              >
                Cancelar
              </Button>

              <Button
                name="submit"
                type="button"
                onClick={onSubmit}
                disabled={updateSupportTicketIsPending}
                isLoading={updateSupportTicketIsPending}
              >
                Salvar
              </Button>
            </div>
          </div>

          <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
            <div className="flex flex-col gap-y-4">
              <div className="flex items-start justify-between">
                <p className="text-lg font-bold text-white-800">Usuários vinculados</p>

                <ModalAddUsers users={users} setUsers={setUsers} />
              </div>

              <Table
                className={cn('max-h-[14.75rem]', {
                  'min-h-14': users?.length > 0,
                })}
                columns={userColumns()}
                rows={InternalSupportTicketsTransformer.usersTableTransformer()(users, setUsers) || []}
                showEmptyTable
                rowEmptyValue="--"
                page={1}
                totalPages={1}
                isLoadingData={false}
              />
            </div>

            <SelectController
              control={control}
              name="reason"
              label="Motivo"
              errorMessage={errors.reason?.message}
              options={supportReasonOptions || []}
              onInputChange={setSearchSupportReason}
              isDisabled
            />

            <div className="flex flex-col gap-y-4">
              <div className="flex items-start justify-between">
                <p className="text-lg font-bold text-white-800">Chamados vinculados</p>

                <ModalAddSupportTickets
                  supportTickets={supportTickets}
                  setSupportTickets={setSupportTickets}
                  hideTicketIds={[supportTicketData?.id || '']}
                />
              </div>

              <Table
                className={cn('max-h-[14.75rem]', {
                  'min-h-14': supportTickets?.length > 0,
                })}
                columns={supportTicketColumns}
                rows={
                  InternalSupportTicketsTransformer.supportTicketsTableTransformer()({
                    supportTickets,
                    setSupportTickets,
                    actionType: 'remove',
                  }) || []
                }
                showEmptyTable
                rowEmptyValue="--"
                page={1}
                totalPages={1}
                isLoadingData={false}
              />
            </div>

            <TextField
              label="Assunto"
              {...register('subject_line')}
              errorMessage={errors.subject_line?.message}
              disabled
            />

            <TextArea
              rows={16}
              maxLength={2048}
              errorMessage={errors.content?.message}
              {...register('content')}
              disabled
            />

            {!!supportTicketMessagesData?.[0]?.documents?.length && (
              <DocumentFileList
                files={supportTicketMessagesData[0].documents.map((document) => ({
                  fileName: getFilenameFromUrl(document.url),
                  currentFileUrl: document.url,
                }))}
              />
            )}
          </Box>
        </>
      )}

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </>
  )
}

export default InternalSupportUpdate
